import "./SubcontractingCompanyForm.scss";
import Button from "core/buttons/button/Button";
import ContactCompanyInfoForm from "components/form/subcontracting-company/ContactCompanyInfoForm";
import InfoContext from "pages/InfoContext";
import SubcontractingCompanyInfoForm from "components/form/subcontracting-company/SubcontractingCompanyInfoForm";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import {
  SUBCONTRACTING_COMPANY_DEFAULT_VALUES,
  SUBCONTRACTING_COMPANY_FORM_LABELS,
  SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS,
} from "constants/forms";
import { formatReceivedData } from "utils/subCompanyHelper";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useApiFieldErrors } from "utils/useApiFieldErrors";
import { useUserContext } from "api/user/UserProvider";
import { renderInputEmail } from "components/form/CommonInputs";
import { POPUP_STATUS, QUERY_STATUS } from "constants/constants";
import { FIELD_REQUIRED } from "constants/popup-codes";
import PropTypes from "prop-types";
import colors from "assets/styles/mixins/colors.scss";

const PersonalSubcontractingCompany = ({
  readOnly,
  setReadOnly,
  onSubmit,
  partnerConnection,
  getStatus,
  getCode,
  id,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
    unregister,
    setError,
  } = useForm({
    defaultValues: SUBCONTRACTING_COMPANY_DEFAULT_VALUES,
  });
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);
  const {
    subcontractorCompany,
    dataCountry,
    lang,
    editSubcontractorCompanyStatus,
    slug,
  } = useContext(InfoContext);

  const [cellHighlight, setCellHighlight] = useState(false);
  const [isValidated, setIsValidated] = useState(false);

  useEffect(() => {
    !readOnly && isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  const { setApiErrorResponse } = useUserContext();

  useApiFieldErrors(setError, setReadOnly);

  const onError = (errors) => {
    if (!_.isEmpty(errors)) {
      getStatus(POPUP_STATUS.ERROR);
      getCode(FIELD_REQUIRED);
    }
    if (!partnerConnection) unregister("partner_email");
  };
  const initForm = () => {
    const defaultKeys = Object.keys(SUBCONTRACTING_COMPANY_DEFAULT_VALUES);
    const formattedReceivedData = formatReceivedData({
      data: subcontractorCompany,
      nationalities: dataCountry,
    });
    Object.keys(formattedReceivedData).forEach((elm) => {
      if (defaultKeys.includes(elm) && formattedReceivedData[elm]) {
        setValue(elm, formattedReceivedData[elm]);
      }
    });
    const nationalityKey = lang === "fr" ? "nationality_fr" : "nationality_eng";
    setValue("nationality", [
      {
        label: subcontractorCompany.nationality?.[nationalityKey],
        value: subcontractorCompany?.nationality?.id,
      },
    ]);
  };

  useEffect(() => {
    if (!_.isEmpty(subcontractorCompany)) {
      initForm();
    }
  }, [subcontractorCompany, dataCountry, lang]);

  const onReset = () => {
    initForm();
    clearErrors();
    setIsValidated(false);
    setCellHighlight(false);
    setReadOnly(true);
    setApiErrorResponse(null);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (!partnerConnection) {
      unregister("partner_email");
    }
  }, [partnerConnection]);

  const onGuardedSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await onSubmit(info);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <form
      id="create-subcontracting-company-form"
      className="subcontracting-company"
      onSubmit={handleSubmit(onGuardedSubmit, onError)}
    >
      {partnerConnection && (
        <div
          className={`email-cell ${
            !watch("partner_email") && cellHighlight && "errorCellColor"
          }`}
        >
          {renderInputEmail({
            control,
            errors,
            label: SUBCONTRACTING_COMPANY_FORM_LABELS.partner_email,
            placeholder: SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS.partner_email,
            register,
            name: "partner_email",
            readOnly,
            required: partnerConnection,
            copyEmail: slug,
            setCode: getCode,
            setStatus: getStatus,
          })}
        </div>
      )}
      <div className="subcontracting-company-form">
        <SubcontractingCompanyInfoForm
          setValue={(field, value) => {
            setValue(field, value);
            clearErrors(field);
          }}
          watch={watch}
          control={control}
          register={register}
          errors={errors}
          readOnly={readOnly}
          id={id}
          cellHighlight={cellHighlight}
        />
        <ContactCompanyInfoForm
          setValue={(field, value) => {
            setValue(field, value);
            clearErrors(field);
          }}
          watch={watch}
          control={control}
          register={register}
          errors={errors}
          readOnly={readOnly}
          cellHighlight={cellHighlight}
        />
      </div>
      {!readOnly && (
        <div style={{ marginTop: "50px" }} className="form-buttons">
          <Button
            className="btn-standard btn-outline"
            type="button"
            onClick={onReset}
            id="button-form-reset"
          >
            <FormattedMessage id="cancel" defaultMessage="Annuler" />
          </Button>
          <Button
            color="btn-medium gray-700"
            type="submit"
            id="button-form-validation"
            onClick={() => setIsValidated(true)}
            backgroundColor={
              editSubcontractorCompanyStatus === QUERY_STATUS.LOADING &&
              colors.disabledGrey
            }
            disabled={
              editSubcontractorCompanyStatus === QUERY_STATUS.LOADING &&
              colors.disabledGrey
            }
          >
            <FormattedMessage
              id="button_form_validation_edition"
              defaultMessage="Valider l'édition"
            />
          </Button>
        </div>
      )}
    </form>
  );
};

export default PersonalSubcontractingCompany;

PersonalSubcontractingCompany.propTypes = {
  readOnly: PropTypes.bool,
  setReadOnly: PropTypes.func,
  onSubmit: PropTypes.func,
  partnerConnection: PropTypes.bool,
  getStatus: PropTypes.func,
  getCode: PropTypes.func,
  id: PropTypes.number,
};
